import { useMemo } from 'react';
import { GenericAlign, GenericSize } from '../../../../constants/generic';
import { INormalizedPlayer, INormalizedTeam } from '../../../../types';
import { SMPContainer, SMPFlex, SMPImage, SMPLinkWrapper, SMPSportEntity, SMPText } from '../../../index';
import { setNationalParticipantBorder, isNationalParticipant } from '@sportal365/smp-widget-utils';

interface ILink {
  url: string;
  urlTarget: string;
  callback?: () => void;
}

export interface IProfileHeadProps {
  player?: INormalizedPlayer;
  team?: INormalizedTeam;
  widgetType: 'player' | 'team';
  size?: GenericSize;
  align?: GenericAlign;
  condensed?: boolean;
  showBottomBorder?: boolean;
  primaryLink?: ILink;
  secondaryLink?: ILink;
  imageBorderColor?: string;
  isMainImageRound?: boolean;
  displaySecondaryIcon?: boolean;
}

export const ProfileHead = ({
  player,
  team,
  widgetType,
  size = GenericSize.DEFAULT,
  align = GenericAlign.LEFT,
  condensed = false,
  showBottomBorder = false,
  primaryLink,
  imageBorderColor,
  isMainImageRound = true,
  displaySecondaryIcon = true,
}: IProfileHeadProps): JSX.Element => {
  const isPlayer = widgetType === 'player';
  const isTeam = widgetType === 'team';
  const rowDirection = align === GenericAlign.LEFT ? 'row' : 'row-reverse';

  const onClick = () => {
    if (primaryLink?.callback) {
      primaryLink.callback();
    }
  };

  const playerName = useMemo(() => {
    if (!player) {
      return '';
    }

    const fullName = player.name;

    if (!condensed) {
      return fullName;
    }

    const nameParts = fullName.split(' ');

    // When condensed, return last name only
    return nameParts[nameParts.length - 1];
  }, [condensed, player]);

  const getNameFontSize = (defaultSize: string) => {
    if (size === GenericSize.EXTRA_SMALL) {
      return '$4';
    }

    if (size === GenericSize.SMALL) {
      if (condensed) {
        return '$4';
      }

      return '$6';
    }

    return defaultSize;
  };

  const isTeamNational = isNationalParticipant(team);
  const profileHeadStyles = setNationalParticipantBorder(isTeam && isTeamNational);
  const subheaderHeadStyles = setNationalParticipantBorder((isPlayer && isTeamNational) || (isTeam && !isTeamNational));

  return (
    <>
      {condensed ? (
        <SMPLinkWrapper href={primaryLink?.url} target={primaryLink?.urlTarget}>
          <SMPContainer
            onClick={onClick}
            css={{
              jc: 'flex-start',
              py: size !== GenericSize.EXTRA_SMALL ? '$3' : '$2',
              display: 'flex',
              ai: 'center',
              fd: 'column',
              borderBottom: showBottomBorder ? '1px solid $dividerBorderColor' : 'unset',
              width: '100%',
            }}
          >
            <SMPSportEntity
              className='profile-link'
              css={{
                flex: 'unset',
                jc: 'center',
                br: isMainImageRound ? '$round' : 'unset',
                size: '$6_5',
                backgroundImage: `url("${isTeam ? team?.logo : player?.thumb}")`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100%',
                mb: '$1',
                ...profileHeadStyles,
              }}
            />
            <SMPText
              variant='contrast'
              weight='700'
              css={{
                ta: 'center',
                fontSize: getNameFontSize('unset'),
                '@bp2': { fontSize: getNameFontSize('$8') },
                mb: '$1',
              }}
            >
              {widgetType === 'team' ? team?.name : playerName}
            </SMPText>
            {displaySecondaryIcon && (isPlayer || (isTeam && team && !team.national)) && (
              <SMPSportEntity
                css={{
                  fd: align === GenericAlign.LEFT ? 'row' : 'row-reverse',
                  ai: 'end',
                  jc: 'end',
                  gap: '$1',
                }}
              >
                <SMPImage
                  css={{
                    borderWidth: '1px',
                    bs: 'solid',
                    bc: imageBorderColor ?? 'transparent',
                    br: isPlayer ? 0 : '$round',
                    size: '$3',
                    ...subheaderHeadStyles,
                  }}
                  src={isPlayer ? team?.logo : team?.country?.logo}
                  alt={isPlayer ? team?.name : team?.country?.name?.toUpperCase()}
                />
                <SMPText
                  css={{
                    fontWeight: 'lighter',
                    color: '$progressTextColor',
                    ta: align === GenericAlign.LEFT ? 'unset' : 'end',
                    wordBreak: 'break-word',
                  }}
                  variant='contrast'
                  size='3'
                >
                  {(isPlayer ? team?.name : team?.country?.name)?.[size !== GenericSize.DEFAULT ? 'toString' : 'toUpperCase']()}
                </SMPText>
              </SMPSportEntity>
            )}
          </SMPContainer>
        </SMPLinkWrapper>
      ) : (
        <SMPLinkWrapper href={primaryLink?.url} target={primaryLink?.urlTarget}>
          <SMPContainer
            onClick={onClick}
            css={{
              display: 'flex',
              py: size !== GenericSize.EXTRA_SMALL ? '$3' : '$2',
              ai: 'center',
              fd: rowDirection,
              borderBottom: showBottomBorder ? '1px solid $dividerBorderColor' : 'unset',
              width: '100%',
            }}
          >
            <SMPSportEntity
              className='profile-link'
              css={{
                flex: 'unset',
                justifyContent: align === GenericAlign.LEFT ? 'start' : 'end',
                borderRadius: isMainImageRound ? '$round' : 'unset',
                [align === GenericAlign.LEFT ? 'marginRight' : 'marginLeft']: '$2',
                size: '$8',
                backgroundImage: `url("${isTeam ? team?.logo : player?.thumb}")`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '100%',
                mb: '$1',
                ...profileHeadStyles,
              }}
            />
            <SMPFlex variant='column'>
              <SMPText
                variant='contrast'
                weight='700'
                css={{
                  ta: align === GenericAlign.LEFT ? 'start' : 'end',
                  fontSize: getNameFontSize('unset'),
                  '@bp2': { fontSize: getNameFontSize('$8') },
                  mb: '$1',
                  lh: '40px',
                }}
              >
                {isTeam ? team?.name : playerName}
              </SMPText>
              {displaySecondaryIcon && (isPlayer || (isTeam && team && !team.national)) && (
                <SMPSportEntity
                  css={{
                    fd: align === GenericAlign.LEFT ? 'row' : 'row-reverse',
                    gap: '$1',
                  }}
                >
                  <SMPImage
                    css={{
                      borderWidth: '1px',
                      bs: 'solid',
                      bc: imageBorderColor ?? 'transparent',
                      br: isPlayer ? 0 : '$round',
                      size: '$5',
                      ...subheaderHeadStyles,
                    }}
                    src={isPlayer ? team?.logo : team?.country?.logo}
                    alt={isPlayer ? team?.name : team?.country?.name?.toUpperCase()}
                  />
                  <SMPText
                    css={{
                      fontWeight: 'lighter',
                      color: '$progressTextColor',
                      ta: align === GenericAlign.LEFT ? 'unset' : 'end',
                      wordBreak: 'break-word',
                    }}
                    variant='contrast'
                    size='3'
                  >
                    {(isPlayer ? team?.name : team?.country?.name)?.[size !== GenericSize.DEFAULT ? 'toString' : 'toUpperCase']()}
                  </SMPText>
                </SMPSportEntity>
              )}
            </SMPFlex>
          </SMPContainer>
        </SMPLinkWrapper>
      )}
    </>
  );
};
